import React from "react";
import { createPortal } from "react-dom";
import Header from "../header/AppHeader";
import background from "../../assets/dashboard-bg.jpg";

// Render overlays at document.body so their fixed positioning + z-index escape
// the AdminShell <main> stacking context (main is `relative z-20`, which would
// otherwise trap the modal beneath the `z-50` header and clip its top/bottom).
const Portal = ({ children }) =>
  typeof document === "undefined"
    ? children
    : createPortal(children, document.body);

/* ---------- helpers ---------- */
export const fmtPts = (n) => {
  if (n >= 100000) return `${(n / 100000).toFixed(1).replace(/\.0$/, "")}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n || 0);
};

export const timeAgo = (date) => {
  if (!date) return "";
  const diff = Date.now() - new Date(date).getTime();
  if (Number.isNaN(diff)) return "";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("default", { month: "short", day: "numeric" }) : "—";

export const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

// Tiers are earned as a SHARE of the total points available across all live
// tasks, not as fixed point totals. The pool (totalAvailablePoints) starts at 0
// and grows as admins publish tasks. A CA who has earned >= 80% of the pool is
// GOLD, >= 65% is SILVER, >= 50% is BRONZE. Below 50% (or before any tasks
// exist) a CA hasn't earned a tier yet. Thresholds are ascending by `pct`.
export const TIERS = [
  { name: "BRONZE", pct: 0.5 },
  { name: "SILVER", pct: 0.65 },
  { name: "GOLD", pct: 0.8 },
];

// tierFor(earnedPoints, totalAvailablePoints). Returns "UNRANKED" when there's
// no pool yet, or the CA hasn't cleared the 50% BRONZE threshold.
export const tierFor = (pts, totalAvailable) => {
  const pool = Number(totalAvailable) || 0;
  if (pool <= 0) return "UNRANKED";
  const ratio = (Number(pts) || 0) / pool;
  let name = "UNRANKED";
  for (const t of TIERS) if (ratio >= t.pct) name = t.name;
  return name;
};
export const TIER_CLS = {
  GOLD: "bg-amber-100 text-amber-700",
  SILVER: "bg-gray-200 text-gray-600",
  BRONZE: "bg-orange-100 text-orange-700",
  UNRANKED: "bg-black/5 text-black/40",
};

export const TASK_TYPES = ["Social Media", "Registration", "Offline", "Creative"];
export const STATUS_OPTIONS = [
  { key: "active", label: "Active" },
  { key: "closed", label: "Closed" },
  { key: "draft", label: "Draft" },
];

export const ANNOUNCEMENT_TAGS = ["Activation", "Update", "Info"];
export const TAG_CLS = {
  Activation: "bg-[#D15B28]/15 text-[#D15B28]",
  Update: "bg-teal-100 text-teal-700",
  Info: "bg-sky-100 text-sky-700",
};

/* ---------- shared styles ---------- */
export const inputCls =
  "w-full bg-white border border-black/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#D15B28] transition";
export const labelCls = "text-[11px] font-bold tracking-widest text-[#2B1B17]/40 uppercase";
export const primaryBtn =
  "bg-[#D15B28] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#b04a1f] transition disabled:opacity-60";

/* ---------- primitives ---------- */
export const StatCard = ({ label, value, sub, valueClassName = "text-5xl" }) => (
  <div className="bg-[#F5EFEB]/90 backdrop-blur-md rounded-sm p-2 shadow-xl border border-white/20 flex flex-col justify-between min-h-[150px]">
    <p className="text-[11px] font-extrabold tracking-widest text-[#2B1B17]/50 uppercase">{label}</p>
    <p className={`${valueClassName} font-black text-[#2B1B17] leading-tight mt-2 truncate`}>{value}</p>
    <p className="text-xs text-[#2B1B17]/50 font-medium mt-2">{sub}</p>
  </div>
);

export const Panel = ({ title, sub, action, children, className = "" }) => (
  <div className={`bg-[#F5EFEB]/90 backdrop-blur-md rounded-sm p-6 md:p-7 shadow-xl border border-white/20 flex flex-col ${className}`}>
    <div className="flex justify-between items-start mb-4 flex-shrink-0 gap-3">
      <div>
        <h2 className="text-sm font-black tracking-widest text-[#2B1B17]/60 uppercase">{title}</h2>
        {sub && <p className="text-xs text-black/40 font-medium mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>
    <div className="flex flex-col flex-1 min-h-0">{children}</div>
  </div>
);

export const Modal = ({ title, label, onClose, children, wide = false }) => (
  <Portal>
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`bg-[#FBF8F5] rounded-2xl shadow-2xl w-full ${wide ? "max-w-4xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto p-6 md:p-7`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            {label && <p className="text-[11px] font-black tracking-widest text-[#D15B28] uppercase">{label}</p>}
            <h2 className="text-xl font-black text-[#2B1B17] mt-1">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 transition flex items-center justify-center text-black/50"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  </Portal>
);

/* ---------- result / confirmation popup ---------- */
// Accent palettes for the circular icon badge + status chip on result popups.
export const RESULT_ACCENTS = {
  teal: { ring: "bg-teal-100", icon: "text-teal-600", chip: "bg-teal-100 text-teal-700" },
  olive: { ring: "bg-[#E7E5D2]", icon: "text-[#6B7A35]", chip: "bg-[#E7E5D2] text-[#6B7A35]" },
  peach: { ring: "bg-[#F4E1D6]", icon: "text-[#D15B28]", chip: "bg-[#F4E1D6] text-[#D15B28]" },
  sky: { ring: "bg-sky-100", icon: "text-sky-600", chip: "bg-sky-100 text-sky-700" },
  red: { ring: "bg-red-100", icon: "text-red-500", chip: "bg-red-100 text-red-600" },
};

const CheckIcon = ({ cls }) => (
  <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = ({ cls }) => (
  <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * Centered confirmation popup shown after an admin action succeeds (or a
 * destructive action completes). Renders an icon badge, title, message, an
 * optional status chip, and one or two action buttons.
 *
 *   accent    – key of RESULT_ACCENTS (teal | olive | peach | sky | red)
 *   danger    – when true, shows the ✕ icon instead of the ✓ check
 *   title     – bold heading (e.g. "Task Published!")
 *   message   – supporting copy below the title (string or node)
 *   chip      – optional status pill text (e.g. "Active · +50 pts · Due Jun 25")
 *   primary   – { label, onClick } main button (orange)
 *   secondary – optional { label, onClick } secondary button (neutral)
 */
export const ResultModal = ({
  accent = "teal",
  danger = false,
  title,
  message,
  chip,
  primary,
  secondary,
  onClose,
}) => {
  const a = RESULT_ACCENTS[accent] || RESULT_ACCENTS.teal;
  return (
    <Portal>
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#FBF8F5] rounded-3xl shadow-2xl w-full max-w-md p-8 md:p-9 text-center"
        style={{ animation: "resultPop 0.18s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-16 h-16 rounded-full ${a.ring} flex items-center justify-center mx-auto`}>
          {danger ? <CrossIcon cls={`w-7 h-7 ${a.icon}`} /> : <CheckIcon cls={`w-7 h-7 ${a.icon}`} />}
        </div>

        <h2 className="text-2xl font-black text-[#2B1B17] mt-5">{title}</h2>
        {message && <p className="text-sm text-black/50 mt-2 leading-relaxed">{message}</p>}

        {chip && (
          <div className="mt-4 flex justify-center">
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-md ${a.chip}`}>{chip}</span>
          </div>
        )}

        <div className="border-t border-black/[0.07] mt-7 pt-6 flex items-center gap-3">
          {secondary ? (
            <>
              <button
                onClick={primary.onClick}
                className="flex-1 bg-[#D15B28] text-white text-sm font-semibold py-3 rounded-full hover:bg-[#b04a1f] transition"
              >
                {primary.label}
              </button>
              <button
                onClick={secondary.onClick}
                className="flex-1 bg-black/[0.06] text-[#2B1B17] text-sm font-semibold py-3 rounded-full hover:bg-black/10 transition"
              >
                {secondary.label}
              </button>
            </>
          ) : (
            <button
              onClick={primary.onClick}
              className="w-full bg-[#D15B28] text-white text-sm font-semibold py-3 rounded-full hover:bg-[#b04a1f] transition"
            >
              {primary.label}
            </button>
          )}
        </div>
      </div>
    </div>
    </Portal>
  );
};

export const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-sm font-semibold px-4 py-2 rounded-full transition ${
      active ? "bg-[#D15B28] text-white" : "bg-black/[0.06] text-black/60 hover:bg-black/10"
    }`}
  >
    {children}
  </button>
);

export const Toggle = ({ on, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-12 h-6 rounded-full transition relative flex-shrink-0 ${on ? "bg-[#D15B28]" : "bg-black/20"}`}
    aria-pressed={on}
  >
    <span
      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${on ? "left-[26px]" : "left-0.5"}`}
    />
  </button>
);

/* ---------- page scaffold (background + header + hero) ---------- */
export const AdminShell = ({ tab, title, subtitle, actions, children }) => (
  <div
    className="min-h-[100dvh] w-full overflow-x-hidden lg:h-screen lg:w-screen lg:overflow-hidden bg-center bg-cover bg-no-repeat text-[#2B1B17] font-sans antialiased flex flex-col"
    style={{ backgroundImage: `url(${background})` }}
  >
    {/* Header + hero (fixed on desktop; scrolls with the page on mobile) */}
    <Header admin adminTab={tab} />

    <section className="px-6 md:px-12 pt-10 md:pt-14 pb-5 max-w-[1600px] mx-auto w-full flex-shrink-0">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="text-white flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5 max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-black tracking-tight drop-shadow-sm">{title}</h1>
          {subtitle && (
            <p className="text-sm md:text-base text-white/90 leading-relaxed font-normal drop-shadow-sm sm:max-w-[20rem]">
              {subtitle}
            </p>
          )}
        </div>
        {actions && <div className="flex-shrink-0 flex items-center gap-3">{actions}</div>}
      </div>
    </section>

    {/* Content area — the page scrolls on mobile; only this area scrolls on desktop */}
    <main className="overflow-y-auto lg:flex-1 lg:min-h-0 max-w-[1600px] mx-auto px-6 md:px-12 w-full relative z-20 flex flex-col gap-5 pb-12">
      {children}
    </main>
  </div>
);

export const Loading = ({ label = "Loading..." }) => (
  <div className="min-h-screen bg-[#0E0705] text-[#F8F5F2] flex items-center justify-center font-sans">
    <div className="text-lg opacity-70 animate-pulse">{label}</div>
  </div>
);
